import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatCents,
} from '../components/ui';

interface Pipeline {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface Stage {
  id: string;
  pipelineId: string;
  name: string;
  position: number;
  color?: string;
  winProbability?: number;
}

interface Opportunity {
  id: string;
  pipelineId: string;
  stageId: string;
  name: string;
  contactId?: string;
  value?: number;
  status: 'open' | 'won' | 'lost' | 'archived';
  assignedTo?: string;
  createdAt: string;
}

export function PipelinesPage() {
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [showCreatePipeline, setShowCreatePipeline] = useState(false);
  const [showCreateOpp, setShowCreateOpp] = useState(false);
  const [editingOpp, setEditingOpp] = useState<Opportunity | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const qc = useQueryClient();

  const pipelinesQ = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<{ data: Pipeline[] }>('/pipelines').catch(() => ({ data: [] })),
  });

  const pipelines = pipelinesQ.data?.data ?? [];

  useEffect(() => {
    if (!activePipelineId && pipelines.length > 0) {
      setActivePipelineId((pipelines.find((p) => p.isDefault) ?? pipelines[0]).id);
    }
  }, [pipelines, activePipelineId]);

  const stagesQ = useQuery({
    queryKey: ['pipeline-stages', activePipelineId],
    queryFn: () =>
      activePipelineId
        ? api.get<{ data: Stage[] }>(`/pipelines/${activePipelineId}/stages`)
        : Promise.resolve({ data: [] }),
    enabled: !!activePipelineId,
  });

  const oppsQ = useQuery({
    queryKey: ['opportunities', activePipelineId],
    queryFn: () =>
      activePipelineId
        ? api.get<{ data: Opportunity[] }>('/opportunities', { pipelineId: activePipelineId })
        : Promise.resolve({ data: [] }),
    enabled: !!activePipelineId,
  });

  const moveMut = useMutation({
    mutationFn: ({ oppId, stageId }: { oppId: string; stageId: string }) =>
      api.patch(`/opportunities/${oppId}/stage`, { stageId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities', activePipelineId] }),
  });

  const stages = (stagesQ.data?.data ?? []).slice().sort((a, b) => a.position - b.position);
  const opps = oppsQ.data?.data ?? [];

  function handleDrop(stageId: string) {
    if (draggedId) {
      moveMut.mutate({ oppId: draggedId, stageId });
      setDraggedId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Pipelines"
        subtitle={pipelines.length === 0 ? 'No pipelines yet' : `${opps.length} opportunities open`}
        action={
          <>
            <Button variant="secondary" onClick={() => setShowCreatePipeline(true)}>
              + Pipeline
            </Button>
            <Button onClick={() => setShowCreateOpp(true)} disabled={!activePipelineId || stages.length === 0}>
              + Opportunity
            </Button>
          </>
        }
      />

      {pipelines.length > 1 && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
          {pipelines.map((p) => (
            <button
              key={p.id}
              onClick={() => setActivePipelineId(p.id)}
              style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-full)',
                border: '1px solid var(--color-border-primary)',
                background: p.id === activePipelineId ? 'var(--color-brand-primary)' : 'var(--color-surface-primary)',
                color: p.id === activePipelineId ? 'white' : 'var(--color-text-primary)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {pipelinesQ.isLoading ? (
        <Spinner />
      ) : pipelines.length === 0 ? (
        <Card>
          <EmptyState
            title="No pipelines yet"
            description="Pipelines track opportunities through your sales process."
            action={<Button onClick={() => setShowCreatePipeline(true)}>+ New pipeline</Button>}
          />
        </Card>
      ) : stages.length === 0 ? (
        <Card>
          <EmptyState
            title="This pipeline has no stages"
            description="Add stages so you can move opportunities through the funnel."
            action={
              <Button onClick={() => activePipelineId && createDefaultStages(activePipelineId, qc)}>
                Add starter stages
              </Button>
            }
          />
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${stages.length}, minmax(260px, 1fr))`,
            gap: 'var(--space-3)',
            overflowX: 'auto',
            paddingBottom: 'var(--space-4)',
          }}
        >
          {stages.map((stage) => {
            const stageOpps = opps.filter((o) => o.stageId === stage.id && o.status === 'open');
            const total = stageOpps.reduce((sum, o) => sum + (o.value ?? 0), 0);
            return (
              <div
                key={stage.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(stage.id)}
                style={{
                  background: 'var(--color-surface-secondary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-3)',
                  minHeight: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color ?? 'var(--color-brand-primary)' }} />
                    <strong style={{ fontSize: 'var(--text-sm)' }}>{stage.name}</strong>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{stageOpps.length}</span>
                  </div>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>{formatCents(total)}</span>
                </div>
                {stageOpps.map((o) => (
                  <div
                    key={o.id}
                    draggable
                    onDragStart={() => setDraggedId(o.id)}
                    onClick={() => setEditingOpp(o)}
                    style={{
                      background: 'var(--color-surface-primary)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3)',
                      border: '1px solid var(--color-border-primary)',
                      cursor: 'grab',
                      boxShadow: 'var(--shadow-sm)',
                    }}
                  >
                    <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{o.name}</div>
                    {o.value && (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-brand-primary)', marginTop: 4 }}>
                        {formatCents(o.value)}
                      </div>
                    )}
                  </div>
                ))}
                {stageOpps.length === 0 && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 'var(--space-3)' }}>
                    Drop opportunities here
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreatePipeline && (
        <CreatePipelineModal
          onClose={() => setShowCreatePipeline(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ['pipelines'] });
            setShowCreatePipeline(false);
            setActivePipelineId(id);
          }}
        />
      )}

      {showCreateOpp && activePipelineId && stages.length > 0 && (
        <OpportunityModal
          pipelineId={activePipelineId}
          stages={stages}
          onClose={() => setShowCreateOpp(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['opportunities', activePipelineId] });
            setShowCreateOpp(false);
          }}
        />
      )}

      {editingOpp && (
        <OpportunityModal
          pipelineId={editingOpp.pipelineId}
          stages={stages}
          opportunity={editingOpp}
          onClose={() => setEditingOpp(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['opportunities', activePipelineId] });
            setEditingOpp(null);
          }}
        />
      )}
    </div>
  );
}

async function createDefaultStages(pipelineId: string, qc: ReturnType<typeof useQueryClient>) {
  const defaults = [
    { name: 'New', color: '#94a3b8', position: 0, winProbability: 0.1 },
    { name: 'Qualified', color: '#3b82f6', position: 1, winProbability: 0.3 },
    { name: 'Proposal', color: '#a855f7', position: 2, winProbability: 0.5 },
    { name: 'Negotiation', color: '#f59e0b', position: 3, winProbability: 0.75 },
    { name: 'Closed Won', color: '#10b981', position: 4, winProbability: 1 },
  ];
  for (const stage of defaults) {
    try {
      await api.post(`/pipelines/${pipelineId}/stages`, stage);
    } catch {
      /* ignore */
    }
  }
  qc.invalidateQueries({ queryKey: ['pipeline-stages', pipelineId] });
}

function CreatePipelineModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => api.post<{ id: string }>('/pipelines', { name }),
    onSuccess: (p) => onSaved(p.id),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New pipeline">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Pipeline name *">
          <Input value={name} onChange={setName} autoFocus placeholder="Sales, Onboarding, Renewals…" />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function OpportunityModal({
  pipelineId,
  stages,
  opportunity,
  onClose,
  onSaved,
}: {
  pipelineId: string;
  stages: Stage[];
  opportunity?: Opportunity;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(opportunity?.name ?? '');
  const [stageId, setStageId] = useState(opportunity?.stageId ?? stages[0].id);
  const [value, setValue] = useState(opportunity?.value ? (opportunity.value / 100).toFixed(2) : '');
  const [status, setStatus] = useState<Opportunity['status']>(opportunity?.status ?? 'open');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        pipelineId,
        stageId,
        name,
        value: value ? Math.round(Number(value) * 100) : undefined,
        status,
      };
      return opportunity ? api.patch(`/opportunities/${opportunity.id}`, body) : api.post('/opportunities', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/opportunities/${opportunity!.id}`),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Delete failed'),
  });

  return (
    <Modal onClose={onClose} title={opportunity ? 'Edit opportunity' : 'New opportunity'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Stage">
          <Select value={stageId} onChange={setStageId} options={stages.map((s) => ({ value: s.id, label: s.name }))} />
        </Field>
        <Field label="Value">
          <Input value={value} onChange={setValue} type="number" step="0.01" min={0} />
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'won', label: 'Won' },
              { value: 'lost', label: 'Lost' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </Field>
        <Field label="Notes">
          <Textarea value={notes} onChange={setNotes} rows={2} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {opportunity ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
